import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import {
  FlaskConical, Target, TrendingUp, TrendingDown, AlertTriangle, Info,
  CheckCircle2, X, RefreshCw, Sparkles, ShieldCheck, BookOpen, Gauge,
  ArrowRight, Play, Ruler, Radar, Beaker, Lightbulb, Eye, ChevronRight,
} from 'lucide-react'
import { getSession } from '../lib/session'

// Growth Lab — the decision engine. It watches the funnel for friction, turns each
// friction into an evidence-backed, human-approved growth experiment grounded in
// behavioral science (never dark patterns), and surfaces a weekly exec brief. Every
// card wears its ethics on its sleeve: the sources it used, the date range, whether
// it's an OBSERVED finding or a HYPOTHESIS, its confidence, and a read-only promise —
// the agent proposes, a human approves, nothing changes spend or pricing on its own.
//
// VISUAL LAYER ONLY: the styling below is the flat "Nunito / Intelligence Briefings"
// aesthetic — soft palette, rounded cards, technique-tag pills, light+dark theme
// tokens — ported verbatim from the dashboard prototype and scoped under `.gl-root`
// so it never leaks. No data wiring or /api/v1/growth call was touched.
const API = '/api/v1/growth'

// ---- scoped stylesheet (dashboard.html tokens, prefixed so nothing leaks) --
const GL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
.gl-root{
  --gl-bg:#FBFBF7; --gl-panel:#FFFFFF; --gl-panel2:#F6F6F0; --gl-line:#ECEBE3;
  --gl-ink:#2E3A45; --gl-mut:#7C8794; --gl-dim:#AEB6BD;
  --gl-accent:#4E9AC4; --gl-up:#4FAE5A; --gl-down:#E24A3B; --gl-warn:#EAB43C; --gl-purple:#8E7CC3;
  --gl-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  --gl-shadow-lg:0 10px 24px rgba(46,58,69,.10),0 34px 66px rgba(46,58,69,.15);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--gl-ink); -webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .gl-root{
  --gl-bg:#0E1116; --gl-panel:#171B22; --gl-panel2:#12161C; --gl-line:#262C36;
  --gl-ink:#E7ECF1; --gl-mut:#95A0AD; --gl-dim:#5C6672;
  --gl-accent:#5FB0DB; --gl-up:#5CC47C; --gl-down:#F0575C; --gl-warn:#EAB43C; --gl-purple:#A796D6;
  --gl-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --gl-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 34px 70px rgba(0,0,0,.6);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .gl-root{
  --gl-bg:#0E1116; --gl-panel:#171B22; --gl-panel2:#12161C; --gl-line:#262C36;
  --gl-ink:#E7ECF1; --gl-mut:#95A0AD; --gl-dim:#5C6672;
  --gl-accent:#5FB0DB; --gl-up:#5CC47C; --gl-down:#F0575C; --gl-warn:#EAB43C; --gl-purple:#A796D6;
  --gl-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --gl-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 34px 70px rgba(0,0,0,.6);
}}
.gl-root *{box-sizing:border-box;}
.gl-title{font-size:24px;font-weight:800;letter-spacing:-.02em;margin:0;display:flex;align-items:center;gap:10px;color:var(--gl-ink);}
.gl-sub{margin:4px 0 0;color:var(--gl-mut);font-size:14px;font-weight:500;line-height:1.5;}
.gl-card{background:var(--gl-panel);border:1px solid var(--gl-line);border-radius:20px;box-shadow:var(--gl-shadow);padding:20px 22px;}
.gl-card.tight{padding:16px 18px;}
.gl-soft{background:var(--gl-panel2);border:1px solid var(--gl-line);border-radius:14px;}
.gl-sec-h{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:var(--gl-mut);display:flex;align-items:center;gap:8px;margin:0;}
.gl-elab{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--gl-mut);margin:0;display:flex;align-items:center;gap:6px;}
.gl-narr{font-size:15px;font-weight:700;line-height:1.5;color:var(--gl-ink);margin:0;}
.gl-body{font-size:14px;font-weight:600;line-height:1.55;color:var(--gl-ink);margin:0;}
.gl-muted{color:var(--gl-mut);font-weight:600;}
.gl-dim{color:var(--gl-dim);font-weight:600;}
.gl-tabnum{font-variant-numeric:tabular-nums;}
/* technique-tag pill (dashboard .tech) — used for the behavioral-principle badge */
.gl-tech{font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;padding:4px 10px;border-radius:8px;background:var(--gl-accent);color:#fff;display:inline-flex;align-items:center;gap:5px;}
/* rounded status/severity pill */
.gl-pill{font-size:11px;font-weight:800;padding:4px 11px;border-radius:20px;display:inline-flex;align-items:center;gap:5px;text-transform:capitalize;}
.gl-chip{font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;background:var(--gl-panel2);border:1px solid var(--gl-line);color:var(--gl-mut);display:inline-flex;align-items:center;gap:5px;white-space:nowrap;}
.gl-mchip{font-size:11px;font-weight:800;padding:5px 11px;border-radius:9px;background:var(--gl-panel2);border:1px solid var(--gl-line);color:var(--gl-mut);display:inline-flex;align-items:center;gap:6px;white-space:nowrap;}
.gl-icobox{flex:none;width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;}
.gl-num{flex:none;width:22px;height:22px;border-radius:50%;color:#fff;font-size:11px;font-weight:800;display:flex;align-items:center;justify-content:center;}
/* buttons */
.gl-btn{font-family:Nunito,system-ui,sans-serif;font-size:13px;font-weight:800;border-radius:12px;padding:9px 15px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;border:1px solid var(--gl-line);background:var(--gl-panel);color:var(--gl-ink);transition:.16s;box-shadow:var(--gl-shadow);}
.gl-btn:hover{transform:translateY(-1px);}
.gl-btn:disabled{opacity:.6;cursor:default;transform:none;}
.gl-btn.sm{font-size:12px;padding:7px 13px;border-radius:10px;}
.gl-btn.primary{background:var(--gl-accent);color:#fff;border-color:transparent;}
.gl-btn.ghost{background:none;border:none;box-shadow:none;color:var(--gl-mut);padding:7px 9px;font-weight:700;}
.gl-btn.ghost:hover{color:var(--gl-ink);}
.gl-btn.link{background:none;border:none;box-shadow:none;padding:0;color:var(--gl-accent);font-size:12.5px;font-weight:800;}
.gl-btn.link:hover{transform:none;filter:brightness(1.08);}
.gl-btn.danger{background:none;border:none;box-shadow:none;color:var(--gl-down);padding:7px 10px;}
.gl-btn.danger:hover{transform:none;filter:brightness(1.05);}
.gl-drawer-toggle{width:100%;text-align:left;cursor:pointer;transition:.16s;}
.gl-drawer-toggle:hover{border-color:var(--gl-accent);transform:translateY(-1px);}
.gl-spin{animation:gl-spin 1s linear infinite;}
@keyframes gl-spin{to{transform:rotate(360deg);}}
.gl-fade{animation:gl-fade .35s ease both;}
@keyframes gl-fade{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
`

let _glStyleInjected = false
function GLStyle() {
  useEffect(() => {
    if (_glStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-gl-style', '1')
    el.textContent = GL_CSS
    document.head.appendChild(el)
    _glStyleInjected = true
  }, [])
  return null
}

// ---- small helpers --------------------------------------------------------
const isNum = (v) => typeof v === 'number' && !Number.isNaN(v)
const titleCase = (s) => String(s || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
// Format an evidence value for display. Returns null for values that should be
// skipped entirely (nested objects other than a {from,to} window) so callers
// never render "[object Object]".
const fmtEv = (v) => {
  if (isNum(v)) return v.toLocaleString()
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    if (v.from != null && v.to != null) return `${v.from} → ${v.to}`
    return null
  }
  if (v == null) return null
  return String(v)
}

// tone tokens: {c: text/accent colour, bg: soft tint} — fixed-hue rgba tints read
// well in both light and dark, exactly like the prototype's pill tints.
const TONE = {
  up:   { c: 'var(--gl-up)',   bg: 'rgba(79,174,90,.15)' },
  down: { c: 'var(--gl-down)', bg: 'rgba(226,74,59,.14)' },
  warn: { c: 'var(--gl-warn)', bg: 'rgba(234,180,60,.16)' },
  acc:  { c: 'var(--gl-accent)', bg: 'rgba(78,154,196,.14)' },
  pur:  { c: 'var(--gl-purple)', bg: 'rgba(142,124,195,.16)' },
  mut:  { c: 'var(--gl-mut)',  bg: 'rgba(124,135,148,.14)' },
}
const pill = (t) => ({ background: t.bg, color: t.c })
const softTint = (t) => ({ background: t.bg, borderColor: t.bg })

// Severity → tone + icon, mirrored from Marketing Brain.
const SEV = {
  critical: { t: TONE.down, icon: AlertTriangle },
  high:     { t: TONE.down, icon: AlertTriangle },
  medium:   { t: TONE.warn, icon: AlertTriangle },
  warning:  { t: TONE.warn, icon: AlertTriangle },
  low:      { t: TONE.acc,  icon: Info },
  info:     { t: TONE.acc,  icon: Info },
}
const sev = (s) => SEV[String(s || 'medium').toLowerCase()] || SEV.medium

// Experiment status → tone + which lifecycle actions are offered.
const STATUS = {
  draft:    { t: TONE.mut,  label: 'Draft' },
  approved: { t: TONE.acc,  label: 'Approved' },
  running:  { t: TONE.pur,  label: 'Running' },
  measured: { t: TONE.up,   label: 'Measured' },
  rejected: { t: TONE.down, label: 'Rejected' },
  stopped:  { t: TONE.warn, label: 'Stopped' },
}
const statusOf = (s) => STATUS[String(s || 'draft').toLowerCase()] || STATUS.draft
// Which action buttons make sense at each stage.
const ACTIONS_FOR = {
  draft:    [['approve', 'Approve'], ['reject', 'Reject']],
  approved: [['start', 'Start'], ['reject', 'Reject']],
  running:  [['measure', 'Measure']],
  measured: [],
  rejected: [],
  stopped:  [],
}

const confTone = (c) => {
  const s = String(c || '').toLowerCase()
  if (s === 'high') return TONE.up
  if (s === 'low') return TONE.warn
  return TONE.acc
}

export default function GrowthLab() {
  const session = getSession()

  return (
    <div className="gl-root space-y-8">
      <GLStyle />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="gl-title">
            <FlaskConical size={24} style={{ color: 'var(--gl-accent)' }} /> Growth Lab
          </h2>
          <p className="gl-sub">
            Detected friction → evidence-backed, human-approved experiments. Grounded in behavioral science, never dark patterns.
          </p>
        </div>
        <span className="gl-pill" style={pill(TONE.up)}>
          <ShieldCheck size={13} /> Read-only · human-approved
        </span>
      </div>

      {!session?.namespaceId && (
        <div className="gl-card" style={{ textAlign: 'center', padding: '48px 22px' }}>
          <FlaskConical size={32} style={{ color: 'var(--gl-dim)', margin: '0 auto 12px' }} />
          <p className="gl-muted" style={{ margin: 0 }}>Connect a workspace to activate the Growth Lab.</p>
        </div>
      )}

      {session?.namespaceId && (
        <>
          <WeeklyBrief />
          <FrictionFeed />
          <Experiments />
          <PlaybookDrawer />
        </>
      )}
    </div>
  )
}

// ---- WEEKLY BRIEF ---------------------------------------------------------
function WeeklyBrief() {
  const [brief, setBrief] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    axios.get(`${API}/brief`)
      .then(r => setBrief(r.data || {}))
      .catch(() => setBrief({}))
      .finally(() => setLoading(false))
  }, [])

  const opportunities = Array.isArray(brief?.top_opportunities) ? brief.top_opportunities : []
  const risks = Array.isArray(brief?.top_risks) ? brief.top_risks : []
  const label = (x) => typeof x === 'string' ? x : (x?.title || x?.headline || x?.summary || titleCase(x?.kind) || '—')

  return (
    <div className="gl-card gl-fade" style={{ background: 'linear-gradient(160deg, rgba(78,154,196,.06), transparent 60%)' }}>
      <div className="flex items-center justify-between gap-2" style={{ marginBottom: 8 }}>
        <span className="gl-sec-h">
          <Sparkles size={16} style={{ color: 'var(--gl-accent)' }} /> Weekly decision brief
        </span>
        {brief?.period && (
          <span className="gl-dim" style={{ fontSize: 12 }}>
            {brief.period.from || brief.period.label || ''}{brief.period.to ? ` → ${brief.period.to}` : ''}
          </span>
        )}
      </div>
      <p className="gl-narr" style={{ fontSize: 18 }}>
        {loading ? 'Reading the funnel…' : (brief?.headline || 'Not enough evidence yet — run a friction scan to build the brief.')}
      </p>
      {typeof brief?.summary === 'string' && brief.summary && <p className="gl-sub" style={{ marginTop: 8 }}>{brief.summary}</p>}

      <div className="grid md:grid-cols-2 gap-4" style={{ marginTop: 20 }}>
        <div style={{ ...softTint(TONE.up), border: '1px solid', borderRadius: 14, padding: 16 }}>
          <p className="gl-elab" style={{ color: 'var(--gl-up)', marginBottom: 10 }}>
            <TrendingUp size={13} /> Top opportunities
          </p>
          {opportunities.length === 0 && <p className="gl-dim" style={{ fontSize: 13, margin: 0 }}>None surfaced yet.</p>}
          <ol className="space-y-2" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {opportunities.slice(0, 3).map((o, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="gl-num" style={{ marginTop: 1, background: 'var(--gl-up)' }}>{i + 1}</span>
                <p className="gl-body" style={{ fontSize: 13.5 }}>{label(o)}</p>
              </li>
            ))}
          </ol>
        </div>
        <div style={{ ...softTint(TONE.down), border: '1px solid', borderRadius: 14, padding: 16 }}>
          <p className="gl-elab" style={{ color: 'var(--gl-down)', marginBottom: 10 }}>
            <TrendingDown size={13} /> Top risks
          </p>
          {risks.length === 0 && <p className="gl-dim" style={{ fontSize: 13, margin: 0 }}>None surfaced yet.</p>}
          <ol className="space-y-2" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {risks.slice(0, 3).map((r, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="gl-num" style={{ marginTop: 1, background: 'var(--gl-down)' }}>{i + 1}</span>
                <p className="gl-body" style={{ fontSize: 13.5 }}>{label(r)}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  )
}

// ---- FRICTION FEED --------------------------------------------------------
function FrictionFeed() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [draftingId, setDraftingId] = useState('')

  const load = () => {
    setLoading(true)
    axios.get(`${API}/frictions`, { params: { status: 'open' } })
      .then(r => setItems(r.data.frictions || r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const scan = async () => {
    setScanning(true)
    try {
      const r = await axios.post(`${API}/scan`)
      const found = (r.data.frictions || []).length
      toast.success(found ? `Detected ${found} friction${found === 1 ? '' : 's'}` : 'Scan complete — no new friction')
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Scan failed') }
    finally { setScanning(false) }
  }

  // Turn one friction straight into a drafted experiment.
  const draft = async (f) => {
    setDraftingId(f.id)
    try {
      await axios.post(`${API}/experiments`, { friction_id: f.id })
      toast.success('Experiment drafted — see below')
      window.dispatchEvent(new CustomEvent('growth-experiments-refresh'))
    } catch (err) { toast.error(err.response?.data?.error || 'Could not draft experiment') }
    finally { setDraftingId('') }
  }

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <h3 className="gl-sec-h">
          <Radar size={14} /> Friction feed
        </h3>
        <button onClick={scan} disabled={scanning} className="gl-btn sm">
          <RefreshCw size={13} className={scanning ? 'gl-spin' : ''} /> {scanning ? 'Scanning…' : 'Scan for friction'}
        </button>
      </div>

      {loading && items.length === 0 && (
        <div className="gl-card gl-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>Scanning the funnel…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="gl-card" style={{ textAlign: 'center', padding: '40px 22px' }}>
          <CheckCircle2 size={28} style={{ color: 'var(--gl-up)', margin: '0 auto 8px', opacity: .8 }} />
          <p className="gl-muted" style={{ fontSize: 13.5, margin: 0 }}>No open friction detected. Run a scan to check the latest funnel.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(f => {
          const c = sev(f.severity)
          const ev = f.evidence && typeof f.evidence === 'object' ? f.evidence : {}
          const evKeys = Object.keys(ev).filter(k => fmtEv(ev[k]) != null).slice(0, 4)
          return (
            <div key={f.id} className="gl-card tight gl-fade" style={{ borderLeft: `4px solid ${c.t.c}` }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3 min-w-0">
                  <span className="gl-icobox" style={{ ...pill(c.t), marginTop: 1 }}><c.icon size={16} /></span>
                  <div className="min-w-0">
                    <p className="gl-narr" style={{ fontSize: 15 }}>{f.title || titleCase(f.kind)}</p>
                    <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 6 }}>
                      <span className="gl-chip">{titleCase(f.kind)}</span>
                      {f.segment && <span className="gl-chip">{f.segment}</span>}
                      {f.metric && <span className="gl-chip">{f.metric}</span>}
                    </div>
                    {evKeys.length > 0 && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1" style={{ marginTop: 8 }}>
                        {evKeys.map(k => (
                          <span key={k} className="gl-muted" style={{ fontSize: 12 }}>
                            <span className="gl-dim">{titleCase(k)}:</span>{' '}
                            <span className="gl-tabnum" style={{ fontWeight: 800, color: 'var(--gl-ink)' }}>{fmtEv(ev[k])}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className="gl-pill" style={pill(c.t)}>{f.severity || 'medium'}</span>
                  <button onClick={() => draft(f)} disabled={draftingId === f.id} className="gl-btn link">
                    <Beaker size={13} /> {draftingId === f.id ? 'Drafting…' : 'Draft experiment'}
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---- EXPERIMENTS ----------------------------------------------------------
function Experiments() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)

  const load = () => {
    setLoading(true)
    axios.get(`${API}/experiments`)
      .then(r => setItems(r.data.experiments || r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
    const h = () => load()
    window.addEventListener('growth-experiments-refresh', h)
    return () => window.removeEventListener('growth-experiments-refresh', h)
  }, [])

  const generate = async () => {
    setGenerating(true)
    try {
      const r = await axios.post(`${API}/experiments/scan`)
      const n = (r.data.experiments || []).length
      toast.success(n ? `Generated ${n} experiment${n === 1 ? '' : 's'}` : 'No open friction to turn into experiments')
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Could not generate experiments') }
    finally { setGenerating(false) }
  }

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <h3 className="gl-sec-h">
          <FlaskConical size={14} /> Experiments
        </h3>
        <button onClick={generate} disabled={generating} className="gl-btn primary sm">
          {generating ? <RefreshCw size={13} className="gl-spin" /> : <Sparkles size={13} />}
          {generating ? 'Generating…' : 'Generate experiments'}
        </button>
      </div>

      {loading && items.length === 0 && (
        <div className="gl-card gl-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>Loading experiments…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="gl-card" style={{ textAlign: 'center', padding: '40px 22px' }}>
          <Beaker size={28} style={{ color: 'var(--gl-dim)', margin: '0 auto 8px' }} />
          <p className="gl-muted" style={{ fontSize: 13.5, margin: 0 }}>No experiments yet. Generate from detected friction — the agent will only propose what the evidence supports.</p>
        </div>
      )}

      <div className="space-y-4">
        {items.map(x => <ExperimentCard key={x.id} exp={x} onChange={load} />)}
      </div>
    </div>
  )
}

function ExperimentCard({ exp, onChange }) {
  const [busy, setBusy] = useState('')
  const st = statusOf(exp.status)
  const isObserved = String(exp.kind || 'hypothesis').toLowerCase() === 'observed'
  const variants = Array.isArray(exp.variants) ? exp.variants : []
  const sources = (Array.isArray(exp.sources) ? exp.sources : [])
    .map(s => typeof s === 'string' ? s : (s?.provider || s?.label || '')).filter(Boolean)
  const ev = exp.evidence && typeof exp.evidence === 'object' ? exp.evidence : {}
  const evKeys = Object.keys(ev).filter(k => fmtEv(ev[k]) != null).slice(0, 6)
  const actions = ACTIONS_FOR[String(exp.status || 'draft').toLowerCase()] || []
  const kindTone = isObserved ? TONE.up : TONE.acc

  const act = async (verb) => {
    setBusy(verb)
    try {
      await axios.post(`${API}/experiments/${exp.id}/${verb}`)
      toast.success(`${titleCase(verb)}d`)
      onChange()
    } catch (err) { toast.error(err.response?.data?.error || `Could not ${verb}`) }
    finally { setBusy('') }
  }

  const recordResult = async () => {
    const summary = window.prompt('Record the measured result (what did the data show?)')
    if (!summary || !summary.trim()) return
    setBusy('result')
    try {
      await axios.post(`${API}/experiments/${exp.id}/result`, { result: { summary: summary.trim(), recorded_at: new Date().toISOString() } })
      toast.success('Result recorded')
      onChange()
    } catch (err) { toast.error(err.response?.data?.error || 'Could not record result') }
    finally { setBusy('') }
  }

  return (
    <div className="gl-card gl-fade">
      {/* header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="gl-narr" style={{ fontSize: 18 }}>{exp.title || 'Untitled experiment'}</p>
          {exp.problem && <p className="gl-sub" style={{ marginTop: 4 }}>{exp.problem}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="gl-pill" style={pill(kindTone)}>
            {isObserved ? <Eye size={11} /> : <Lightbulb size={11} />} {isObserved ? 'Observed' : 'Hypothesis'}
          </span>
          <span className="gl-pill" style={pill(st.t)}>{st.label}</span>
        </div>
      </div>

      {/* behavioral principle badge — styled as the dashboard's 'tech' pill */}
      {(exp.principle || exp.principle_key) && (
        <div style={{ marginTop: 12 }}>
          <span className="gl-tech"><Sparkles size={12} /> {exp.principle || titleCase(exp.principle_key)}</span>
        </div>
      )}

      {/* evidence */}
      {evKeys.length > 0 && (
        <div className="gl-soft" style={{ marginTop: 12, padding: 12 }}>
          <p className="gl-elab" style={{ marginBottom: 8 }}>Evidence</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {evKeys.map(k => (
              <span key={k} className="gl-muted" style={{ fontSize: 12 }}>
                <span className="gl-dim">{titleCase(k)}:</span>{' '}
                <span className="gl-tabnum" style={{ fontWeight: 800, color: 'var(--gl-ink)' }}>{fmtEv(ev[k])}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* hypothesis + proposed change */}
      {exp.hypothesis && (
        <p className="gl-body" style={{ marginTop: 12 }}><span style={{ fontWeight: 800 }}>Hypothesis:</span> {exp.hypothesis}</p>
      )}
      {exp.proposed_change && (
        <p className="gl-body" style={{ marginTop: 6 }}><span style={{ fontWeight: 800 }}>Proposed change:</span> {exp.proposed_change}</p>
      )}

      {/* variants */}
      {variants.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3" style={{ marginTop: 16 }}>
          {variants.map((v, i) => (
            <div key={i} className="gl-soft" style={{ padding: 12 }}>
              <p className="gl-elab" style={{ marginBottom: 6, letterSpacing: '.03em' }}>{v.name || (i === 0 ? 'Control' : `Variant ${String.fromCharCode(64 + i)}`)}</p>
              {v.copy && <p className="gl-body" style={{ fontSize: 13.5 }}>{v.copy}</p>}
              {v.ux && <p className="gl-muted" style={{ fontSize: 12, marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 4 }}><ChevronRight size={12} className="shrink-0" style={{ marginTop: 2, color: 'var(--gl-dim)' }} /> {v.ux}</p>}
            </div>
          ))}
        </div>
      )}

      {/* measurement row */}
      <div className="flex flex-wrap gap-2" style={{ marginTop: 16 }}>
        {exp.metric && <span className="gl-mchip"><Target size={11} /> Metric: {exp.metric}</span>}
        {exp.guardrail && <span className="gl-mchip" style={{ ...pill(TONE.warn), border: '1px solid transparent' }}><ShieldCheck size={11} /> Guardrail: {exp.guardrail}</span>}
        {(exp.duration_days != null) && <span className="gl-mchip"><RefreshCw size={11} /> {exp.duration_days} days</span>}
        {exp.success_threshold && <span className="gl-mchip"><Ruler size={11} /> Target: {exp.success_threshold}</span>}
        {exp.confidence && <span className="gl-mchip" style={{ ...pill(confTone(exp.confidence)), border: '1px solid transparent' }}><Gauge size={11} /> {titleCase(exp.confidence)} confidence</span>}
      </div>

      {/* upside / trade-off */}
      {(exp.expected_upside || exp.trade_off) && (
        <div className="grid sm:grid-cols-2 gap-3" style={{ marginTop: 16 }}>
          {exp.expected_upside && (
            <div style={{ ...softTint(TONE.up), border: '1px solid', borderRadius: 14, padding: 12 }}>
              <p className="gl-elab" style={{ color: 'var(--gl-up)', marginBottom: 6 }}><TrendingUp size={11} /> Expected upside</p>
              <p className="gl-body" style={{ fontSize: 13.5 }}>{exp.expected_upside}</p>
            </div>
          )}
          {exp.trade_off && (
            <div style={{ ...softTint(TONE.warn), border: '1px solid', borderRadius: 14, padding: 12 }}>
              <p className="gl-elab" style={{ color: 'var(--gl-warn)', marginBottom: 6 }}><AlertTriangle size={11} /> Trade-off</p>
              <p className="gl-body" style={{ fontSize: 13.5 }}>{exp.trade_off}</p>
            </div>
          )}
        </div>
      )}

      {/* recorded result */}
      {exp.result && typeof exp.result === 'object' && exp.result.summary && (
        <div style={{ ...softTint(TONE.up), border: '1px solid', borderRadius: 14, padding: 12, marginTop: 16 }}>
          <p className="gl-elab" style={{ color: 'var(--gl-up)', marginBottom: 6 }}><CheckCircle2 size={11} /> Result</p>
          <p className="gl-body" style={{ fontSize: 13.5 }}>{exp.result.summary}</p>
        </div>
      )}

      {/* footer: sources + ethics + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginTop: 16, borderTop: '1px solid var(--gl-line)', paddingTop: 14 }}>
        <div className="flex flex-wrap items-center gap-2">
          {sources.length > 0 && <span className="gl-dim" style={{ fontSize: 12 }}>Sources:</span>}
          {sources.map(s => (
            <span key={s} className="gl-pill" style={{ ...pill(TONE.acc), textTransform: 'capitalize' }}>{s}</span>
          ))}
          <span className="gl-dim" style={{ fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 4 }}>
            <ShieldCheck size={12} /> Read-only — never auto-changes spend, pricing, or audiences.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions.map(([verb, label]) => {
            const primary = verb === 'approve' || verb === 'start'
            const danger = verb === 'reject'
            const Icon = verb === 'approve' ? CheckCircle2 : verb === 'reject' ? X : verb === 'start' ? Play : Ruler
            return (
              <button key={verb} onClick={() => act(verb)} disabled={!!busy}
                className={`gl-btn sm ${primary ? 'primary' : danger ? 'danger' : ''}`}>
                <Icon size={13} className={busy === verb ? 'gl-spin' : ''} /> {busy === verb ? '…' : label}
              </button>
            )
          })}
          {String(exp.status || '').toLowerCase() === 'measured' && !(exp.result && exp.result.summary) && (
            <button onClick={recordResult} disabled={!!busy} className="gl-btn sm">
              <Ruler size={13} /> {busy === 'result' ? 'Saving…' : 'Record result'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---- PLAYBOOK DRAWER ------------------------------------------------------
function PlaybookDrawer() {
  const [open, setOpen] = useState(false)
  const [concepts, setConcepts] = useState([])
  const [loading, setLoading] = useState(false)

  const load = () => {
    if (concepts.length) return
    setLoading(true)
    axios.get(`${API}/playbook`)
      .then(r => setConcepts(r.data.playbook || r.data.concepts || r.data.items || (Array.isArray(r.data) ? r.data : [])))
      .catch(() => setConcepts([]))
      .finally(() => setLoading(false))
  }

  const toggle = () => { const next = !open; setOpen(next); if (next) load() }

  return (
    <div>
      <button onClick={toggle} className="gl-card tight gl-drawer-toggle flex items-center justify-between">
        <span className="gl-sec-h" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 14, color: 'var(--gl-ink)' }}>
          <BookOpen size={16} style={{ color: 'var(--gl-accent)' }} /> Behavioral playbook
          <span className="gl-dim" style={{ fontSize: 12, fontWeight: 600 }}>— the ethical principles the agent draws from</span>
        </span>
        <ChevronRight size={18} style={{ color: 'var(--gl-mut)', transition: 'transform .2s', transform: open ? 'rotate(90deg)' : 'none' }} />
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          <p className="gl-dim" style={{ fontSize: 11.5, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
            <ShieldCheck size={12} /> Transparent framing only — every concept lists its ethical use. No dark patterns.
          </p>
          {loading && concepts.length === 0 && (
            <div className="gl-card gl-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>Loading playbook…</div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {concepts.map(c => (
              <div key={c.key || c.name} className="gl-card tight gl-fade">
                <p className="gl-narr" style={{ fontSize: 15 }}>{c.name || titleCase(c.key)}</p>
                {c.meaning && <p className="gl-body" style={{ fontSize: 13.5, marginTop: 6, color: 'var(--gl-mut)' }}>{c.meaning}</p>}
                {c.ethical_use && (
                  <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--gl-up)', marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <ShieldCheck size={12} className="shrink-0" style={{ marginTop: 2 }} /> {c.ethical_use}
                  </p>
                )}
                {c.when && (
                  <p className="gl-dim" style={{ fontSize: 12, marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <ArrowRight size={12} className="shrink-0" style={{ marginTop: 2 }} /> {c.when}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
